import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { api } from '@/lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Trash2, MapPin, Phone, Star, Navigation, Loader2, Search, Upload, X } from 'lucide-react';
import { toast } from 'sonner';

// Fix leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const TYPE_OPTS = [
    { value: 'peluqueria', label: '✂️ Peluquería Canina' },
    { value: 'veterinaria', label: '🏥 Veterinaria' },
    { value: 'ambos', label: '🐾 Peluquería & Veterinaria' },
];

const TYPE_BADGE = {
    peluqueria: 'bg-purple-100 text-purple-700',
    veterinaria: 'bg-blue-100 text-blue-700',
    ambos: 'bg-emerald-100 text-emerald-700',
};

const EMPTY_FORM = {
    name: '', type: 'peluqueria', address: '', neighborhood: '',
    phone: '', whatsapp: '', description: '', services: '', images: [],
    lat: null, lng: null, is_active: true, is_featured: false,
};

const BA_CENTER = [-34.6037, -58.3816];

// ─── Galería de imágenes con upload directo (hasta 4 fotos) ──────────────────
const MAX_IMAGES = 4;

function ImageGallery({ images, onChange }) {
    const [uploading, setUploading] = useState(false);
    const inputRef = useRef();

    const handleFiles = async (files) => {
        const arr = Array.from(files);
        for (const file of arr) {
            if (images.length >= MAX_IMAGES) { toast.warning(`Máximo ${MAX_IMAGES} fotos`); break; }
            setUploading(true);
            try {
                const fd = new FormData();
                fd.append('file', file);
                const r = await api.post('/admin/upload/image', fd);
                onChange([...images, r.data.url]);
            } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al subir imagen');
            }
            setUploading(false);
        }
    };

    const remove = (i) => onChange(images.filter((_, idx) => idx !== i));

    return (
        <div className="mt-1">
            <div className="grid grid-cols-4 gap-2">
                {images.map((url, i) => (
                    <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-cp-border group">
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        {i === 0 && (
                            <span className="absolute top-1 left-1 bg-cp-secondary text-white text-[9px] font-bold px-1.5 py-0.5 rounded">
                                Principal
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => remove(i)}
                            className="absolute top-1 right-1 bg-black/50 hover:bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                            <X size={12} />
                        </button>
                    </div>
                ))}

                {images.length < MAX_IMAGES && (
                    <div
                        onClick={() => inputRef.current?.click()}
                        className="aspect-square rounded-xl border-2 border-dashed border-cp-border hover:border-cp-primary hover:bg-cp-bg-alt transition-colors cursor-pointer flex flex-col items-center justify-center gap-1"
                    >
                        {uploading
                            ? <Loader2 size={18} className="animate-spin text-cp-primary" />
                            : <>
                                <Upload size={18} className="text-cp-text-muted" />
                                <span className="text-[10px] text-cp-text-muted text-center leading-tight px-1">
                                    Agregar foto
                                </span>
                              </>
                        }
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            className="hidden"
                            onChange={e => handleFiles(e.target.files)}
                            disabled={uploading}
                        />
                    </div>
                )}

                {/* Slots vacíos para completar la grilla */}
                {Array.from({ length: Math.max(0, MAX_IMAGES - images.length - 1) }).map((_, i) => (
                    <div key={`empty-${i}`} className="aspect-square rounded-xl border border-dashed border-cp-border/40 bg-cp-bg-alt/30 flex items-center justify-center text-cp-text-muted/30 text-xs">
                        📸
                    </div>
                ))}
            </div>
            <p className="text-[10px] text-cp-text-muted mt-1.5">La primera foto es la principal · máx. {MAX_IMAGES} fotos · JPG, PNG, WebP hasta 5 MB</p>
        </div>
    );
}

// Vuela el mapa a las coordenadas cuando cambian
function MapController({ position }) {
    const map = useMap();
    useEffect(() => {
        if (position) map.flyTo(position, 16, { duration: 0.8 });
    }, [position, map]);
    return null;
}

// Captura clicks en el mapa
function ClickHandler({ onClick }) {
    useMapEvents({ click: (e) => onClick(e.latlng.lat, e.latlng.lng) });
    return null;
}

function LocationPicker({ lat, lng, onChange }) {
    const position = lat && lng ? [lat, lng] : null;

    return (
        <div className="rounded-xl overflow-hidden border-2 border-dashed border-cp-border hover:border-cp-primary transition-colors" style={{ height: 220 }}>
            <MapContainer
                center={position || BA_CENTER}
                zoom={position ? 15 : 12}
                style={{ width: '100%', height: '100%' }}
                scrollWheelZoom={false}
            >
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <ClickHandler onClick={(lat, lng) => onChange(lat, lng)} />
                {position && <MapController position={position} />}
                {position && <Marker position={position} />}
            </MapContainer>
        </div>
    );
}

export default function AdminLocales() {
    const [locals, setLocals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [geocoding, setGeocoding] = useState(false);

    const load = async () => {
        try {
            const r = await api.get('/admin/locals');
            setLocals(r.data);
        } catch (e) { console.error(e); }
        setLoading(false);
    };
    useEffect(() => { load(); }, []);

    const openNew = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
    const openEdit = (l) => {
        setEditing(l.id);
        setForm({
            name: l.name, type: l.type, address: l.address,
            neighborhood: l.neighborhood || '', phone: l.phone || '',
            whatsapp: l.whatsapp || '', description: l.description || '',
            services: Array.isArray(l.services) ? l.services.join(', ') : '',
            images: Array.isArray(l.images) ? l.images : (l.image ? [l.image] : []),
            lat: l.lat ?? null, lng: l.lng ?? null,
            is_active: l.is_active, is_featured: l.is_featured,
        });
        setDialogOpen(true);
    };

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const geocodeAddress = async () => {
        const query = [form.address, form.neighborhood, 'Buenos Aires, Argentina'].filter(Boolean).join(', ');
        if (!form.address.trim()) { toast.error('Escribí una dirección primero'); return; }
        setGeocoding(true);
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
                { headers: { 'Accept-Language': 'es' } }
            );
            const data = await res.json();
            if (data[0]) {
                const lat = parseFloat(data[0].lat);
                const lng = parseFloat(data[0].lon);
                setForm(f => ({ ...f, lat, lng }));
                toast.success('Ubicación encontrada — ajustá el pin si es necesario');
            } else {
                toast.error('No se encontró la dirección, intentá con otra referencia');
            }
        } catch { toast.error('Error al buscar la dirección'); }
        setGeocoding(false);
    };

    const handleMapClick = (lat, lng) => {
        setForm(f => ({ ...f, lat, lng }));
    };

    const handleSave = async () => {
        if (!form.name || !form.address) { toast.error('Nombre y dirección son obligatorios'); return; }
        if (!form.lat || !form.lng) { toast.error('Buscá la dirección en el mapa para colocar el marcador'); return; }
        setSaving(true);
        const payload = {
            ...form,
            services: form.services ? form.services.split(',').map(s => s.trim()).filter(Boolean) : [],
            images: form.images || [],
        };
        try {
            if (editing) {
                await api.put(`/admin/locals/${editing}`, payload);
                toast.success('Local actualizado');
            } else {
                await api.post('/admin/locals', payload);
                toast.success('Local creado');
            }
            setDialogOpen(false);
            load();
        } catch { toast.error('Error al guardar'); }
        setSaving(false);
    };

    const handleDelete = async (id, name) => {
        if (!window.confirm(`¿Eliminar "${name}"?`)) return;
        try { await api.delete(`/admin/locals/${id}`); toast.success('Local eliminado'); load(); }
        catch { toast.error('Error al eliminar'); }
    };

    const toggleActive = async (l) => {
        const payload = {
            ...l,
            services: Array.isArray(l.services) ? l.services : [],
            images: Array.isArray(l.images) ? l.images : [],
            is_active: !l.is_active,
        };
        try { await api.put(`/admin/locals/${l.id}`, payload); load(); }
        catch { toast.error('Error al actualizar'); }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-heading font-bold text-cp-text-main">Locales</h1>
                    <p className="text-cp-text-muted text-sm mt-0.5">Peluquerías caninas y veterinarias en el mapa</p>
                </div>
                <button onClick={openNew} className="flex items-center gap-2 bg-cp-secondary hover:bg-cp-secondary-hover text-white px-4 py-2.5 rounded-xl text-sm font-semibold">
                    <Plus size={16} /> Agregar local
                </button>
            </div>

            {loading && (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {[1,2,3].map(i => <div key={i} className="h-36 skeleton rounded-xl" />)}
                </div>
            )}

            {!loading && locals.length === 0 && (
                <div className="text-center py-20 text-cp-text-muted bg-white rounded-2xl border border-cp-border">
                    <MapPin size={40} className="mx-auto mb-3 opacity-30" />
                    <p className="font-semibold">No hay locales cargados</p>
                    <p className="text-xs mt-1">Agregá peluquerías o veterinarias para mostrarlas en el mapa</p>
                    <button onClick={openNew} className="mt-4 px-4 py-2 bg-cp-secondary text-white rounded-xl text-sm font-semibold">
                        Agregar el primero
                    </button>
                </div>
            )}

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {locals.map(l => (
                    <div key={l.id} className={`bg-white border rounded-2xl p-4 ${l.is_active ? 'border-cp-border' : 'border-cp-border opacity-60'}`}>
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <div className="w-10 h-10 rounded-xl bg-cp-bg-alt flex items-center justify-center overflow-hidden shrink-0 text-lg">
                                    {l.images?.[0] || l.image
                                        ? <img src={l.images?.[0] || l.image} alt="" className="w-full h-full object-cover" />
                                        : (l.type === 'veterinaria' ? '🏥' : '✂️')
                                    }
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center gap-1.5">
                                        <h3 className="font-heading font-bold text-cp-text-main text-sm truncate">{l.name}</h3>
                                        {l.is_featured && <Star size={11} className="text-amber-400 fill-amber-400 shrink-0" />}
                                    </div>
                                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${TYPE_BADGE[l.type] || 'bg-gray-100 text-gray-600'}`}>
                                        {TYPE_OPTS.find(t => t.value === l.type)?.label || l.type}
                                    </span>
                                </div>
                            </div>
                            <div className="flex gap-1 shrink-0">
                                <button onClick={() => openEdit(l)} className="p-1.5 text-cp-text-muted hover:text-cp-secondary rounded-lg hover:bg-cp-bg-alt"><Pencil size={14} /></button>
                                <button onClick={() => handleDelete(l.id, l.name)} className="p-1.5 text-cp-text-muted hover:text-cp-error rounded-lg hover:bg-cp-bg-alt"><Trash2 size={14} /></button>
                            </div>
                        </div>
                        <p className="text-xs text-cp-text-muted flex items-start gap-1 mb-2 line-clamp-1">
                            <MapPin size={11} className="mt-0.5 shrink-0" />
                            {l.address}{l.neighborhood ? ` · ${l.neighborhood}` : ''}
                        </p>
                        <div className="flex items-center gap-3 text-[10px] text-cp-text-muted mb-3">
                            {l.phone && <span className="flex items-center gap-0.5"><Phone size={10} /> {l.phone}</span>}
                            {l.lat && l.lng
                                ? <span className="flex items-center gap-0.5 text-emerald-600"><Navigation size={10} /> En mapa</span>
                                : <span className="flex items-center gap-0.5 text-amber-500"><Navigation size={10} /> Sin ubicación</span>
                            }
                        </div>
                        <div className="flex items-center justify-between pt-2 border-t border-cp-border">
                            <span className={`text-[10px] font-semibold ${l.is_active ? 'text-emerald-600' : 'text-cp-text-muted'}`}>
                                {l.is_active ? 'Activo' : 'Inactivo'}
                            </span>
                            <Switch checked={l.is_active} onCheckedChange={() => toggleActive(l)} />
                        </div>
                    </div>
                ))}
            </div>

            {/* Modal */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? 'Editar local' : 'Agregar local'}</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4 pt-1">
                        {/* Nombre + Tipo */}
                        <div className="grid grid-cols-2 gap-3">
                            <div className="col-span-2">
                                <Label>Nombre *</Label>
                                <Input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Ej: Peluquería Peluchín" className="mt-1" />
                            </div>
                            <div className="col-span-2">
                                <Label>Tipo *</Label>
                                <select
                                    value={form.type}
                                    onChange={e => set('type', e.target.value)}
                                    className="mt-1 w-full px-3 py-2.5 text-sm border border-cp-border rounded-lg bg-white focus:border-cp-primary outline-none"
                                >
                                    {TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Dirección + geocoding */}
                        <div>
                            <Label>Dirección *</Label>
                            <div className="flex gap-2 mt-1">
                                <Input
                                    value={form.address}
                                    onChange={e => set('address', e.target.value)}
                                    onKeyDown={e => e.key === 'Enter' && geocodeAddress()}
                                    placeholder="Ej: Av. Santa Fe 4290"
                                    className="flex-1"
                                />
                                <Input
                                    value={form.neighborhood}
                                    onChange={e => set('neighborhood', e.target.value)}
                                    placeholder="Barrio"
                                    className="w-28"
                                />
                                <button
                                    type="button"
                                    onClick={geocodeAddress}
                                    disabled={geocoding}
                                    className="px-3 py-2 bg-cp-secondary hover:bg-cp-secondary-hover text-white rounded-lg text-xs font-semibold flex items-center gap-1.5 disabled:opacity-60 whitespace-nowrap"
                                >
                                    {geocoding ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}
                                    Buscar
                                </button>
                            </div>
                        </div>

                        {/* Mapa interactivo */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <Label>Ubicación en el mapa</Label>
                                {form.lat && form.lng
                                    ? <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-1"><Navigation size={10} /> Ubicación marcada</span>
                                    : <span className="text-[10px] text-amber-500 font-medium">Buscá la dirección o hacé click en el mapa</span>
                                }
                            </div>
                            <LocationPicker
                                lat={form.lat}
                                lng={form.lng}
                                onChange={handleMapClick}
                            />
                            <p className="text-[10px] text-cp-text-muted mt-1.5 flex items-center gap-1">
                                <MapPin size={10} /> Hacé click en el mapa para ajustar la ubicación exacta del marcador
                            </p>
                        </div>

                        {/* Contacto */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Teléfono</Label>
                                <Input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="011-1234-5678" className="mt-1" />
                            </div>
                            <div>
                                <Label>WhatsApp</Label>
                                <Input value={form.whatsapp} onChange={e => set('whatsapp', e.target.value)} placeholder="5491112345678" className="mt-1" />
                            </div>
                            <div className="col-span-2">
                                <Label>Fotos del local</Label>
                                <ImageGallery images={form.images} onChange={imgs => set('images', imgs)} />
                            </div>
                        </div>

                        {/* Descripción + servicios */}
                        <div className="space-y-3">
                            <div>
                                <Label>Descripción</Label>
                                <textarea
                                    value={form.description}
                                    onChange={e => set('description', e.target.value)}
                                    rows={2}
                                    placeholder="Breve descripción del local..."
                                    className="mt-1 w-full px-3 py-2 text-sm border border-cp-border rounded-lg focus:border-cp-primary outline-none resize-none"
                                />
                            </div>
                            <div>
                                <Label>Servicios <span className="text-cp-text-muted text-[10px] ml-1">(separados por coma)</span></Label>
                                <Input value={form.services} onChange={e => set('services', e.target.value)} placeholder="Baño, Corte, Pelado, Vacunas..." className="mt-1" />
                            </div>
                        </div>

                        {/* Switches */}
                        <div className="flex items-center gap-6 pt-2 border-t border-cp-border">
                            <label className="flex items-center gap-2 cursor-pointer">
                                <Switch checked={form.is_active} onCheckedChange={v => set('is_active', v)} />
                                <span className="text-sm text-cp-text-main">Activo</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                                <Switch checked={form.is_featured} onCheckedChange={v => set('is_featured', v)} />
                                <span className="text-sm text-cp-text-main flex items-center gap-1">
                                    <Star size={13} className="text-amber-400 fill-amber-400" /> Destacado
                                </span>
                            </label>
                        </div>

                        {/* Botones */}
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setDialogOpen(false)} className="px-4 py-2 text-sm text-cp-text-body hover:bg-cp-bg-alt rounded-lg transition-colors">
                                Cancelar
                            </button>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="px-5 py-2 bg-cp-secondary hover:bg-cp-secondary-hover text-white rounded-xl text-sm font-semibold disabled:opacity-60 flex items-center gap-2"
                            >
                                {saving && <Loader2 size={14} className="animate-spin" />}
                                {editing ? 'Guardar cambios' : 'Crear local'}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
